import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/index.ts";
import { IDeleteUser } from "../../interfaces/others/IDeleteUser";
import { notification } from "../../utils/Notifications.tsx";
import { DeletePartnerSchema } from "../../validation/schemas/DeletePartnerSchema.ts";
import CustomInput from "../formik/CustomInput.tsx";
import { toCamelCase } from "../../hooks/toCameCase.tsx";
import CustomCheckboxField from "../formik/CustomCheckboxField.tsx";
import {
  checkPending,
  saveUnsubscribeRequest,
} from "../../services/unsubscribeRequest.service.ts";
import { cancelProgramingPartner } from "../../services/partner.service.ts";
import { UnsubscribeRequest } from "../../models/UnsubscribeRequest.ts";
import PendingUnsubscribe from "../colaboradores/PendingUnsubscribe.tsx";
import { Partner } from "../../models/Partner.ts";
import { setFetch } from "../../store/slices/colaboradoresSlice.ts";

const DeletePartner: React.FC<IDeleteUser> = ({
  name,
  id,
  programed,
  closeFn,
}) => {
  const dispatch = useDispatch();
  const { fetch } = useSelector((state: RootState) => state.colaboradores);
  const [pendingUnsubscribe, setPendingUnsubscribe] =
    useState<UnsubscribeRequest | null>(null);
  const [partner, setPartner] = useState<Partner | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    checkPendingApproval();
  }, []);

  const checkPendingApproval = async () => {
    try {
      setLoading(true);
      const response = await checkPending(id);
      if (response) {
        setPendingUnsubscribe(response);
        setPartner(response.partner);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const formik = useFormik({
    validationSchema: DeletePartnerSchema,
    initialValues: {
      reasonUnsubscribe: "",
      temporalUnsubscribe: false,
      banDate: getTodayDate(),
      unsubscribeDate: getTodayDate(),
    },
    onSubmit: async (values) => {
      try {
        setLoading(true);
        // Si es baja programada, usar banDate, sino usar unsubscribeDate
        const dateToUse = values.temporalUnsubscribe 
          ? values.banDate 
          : values.unsubscribeDate;
        
        await saveUnsubscribeRequest({
          partner: id,
          reasonUnsubscribe: values.reasonUnsubscribe,
          temporalUnsubscribe: values.temporalUnsubscribe,
          banDate: dateToUse || undefined,
        });
        notification("", `Solicitud de baja enviada para ${name}`, "success");
        dispatch(setFetch(!fetch));
        await checkPendingApproval();
      } catch (error: any) {
        console.error(error);
        notification(
          "Error :(",
          error?.response?.data?.message ||
            `Ocurrio un error al tratar de solicitar la baja de ${toCamelCase(
              name
            )}`,
          "danger"
        );
      } finally {
        setLoading(false);
      }
    },
  });

  const handleCancel = async () => {
    try {
      await cancelProgramingPartner(id);
      notification(
        "Éxito",
        `Baja de ${toCamelCase(name)} cancelada`,
        "success"
      );
      dispatch(setFetch(!fetch));
      closeFn();
    } catch (error) {
      console.error(error);
      notification(
        "Error :(",
        `Ocurrio un error al tratar de cancelar la baja de ${toCamelCase(
          name
        )}`,
        "danger"
      );
    }
  };

  useEffect(() => {
    if (programed) {
      console.log("programed", programed);
      formik.setFieldValue("temporalUnsubscribe", !!programed);
      formik.setFieldValue("banDate", programed.banDate);
      formik.setFieldValue("unsubscribeDate", programed.banDate || getTodayDate());
      formik.setFieldValue("reasonUnsubscribe", programed.reasonUnsubscribe);
    }
  }, []);

  // Sincronizar fechas cuando se marca/desmarca baja programada
  useEffect(() => {
    if (formik.values.temporalUnsubscribe && !formik.values.banDate) {
      // Si se marca baja programada y no hay banDate, copiar unsubscribeDate
      formik.setFieldValue("banDate", formik.values.unsubscribeDate);
    }
  }, [formik.values.temporalUnsubscribe]);

  const handleClose = () => {
    dispatch(setFetch(!fetch));
    closeFn();
  };

  if (loading && !pendingUnsubscribe) {
    return <div>Cargando...</div>;
  }

  if (pendingUnsubscribe && partner) {
    return (
      <PendingUnsubscribe
        partner={partner}
        pendingUnsubscribe={pendingUnsubscribe}
        closeFn={handleClose}
      />
    );
  }

  return (
    <React.Fragment>
      <p className="fs-15px">
        ¿Estás seguro de que deseas solicitar la baja de {toCamelCase(name)}?
      </p>
      <CustomInput
        formik={formik}
        field="reasonUnsubscribe"
        label="Razón de la baja"
        placeholder="Escribe la razón de la baja"
        sm={8}
        smLabel={4}
        disabled={programed}
      />
      <CustomInput
        type="date"
        formik={formik}
        field="unsubscribeDate"
        label="Fecha de baja"
        sm={8}
        smLabel={4}
        disabled={programed}
      />
      <CustomCheckboxField
        field="temporalUnsubscribe"
        formik={formik}
        label="Baja programada"
        mdLabel={4}
        disable={programed}
      />
      {formik.values.temporalUnsubscribe && (
        <CustomInput
          type="date"
          formik={formik}
          field="banDate"
          label="Fecha de baja programada"
          sm={8}
          smLabel={4}
          disabled={programed}
          minDate={new Date().toISOString().split("T")[0]}
        />
      )}

      <div className="d-flex justify-content-end">
        <button
          className="btn btn-secondary me-2"
          onClick={() => handleClose()}
        >
          Cancelar
        </button>
        {programed ? (
          <button className="btn btn-azul me-2" onClick={() => handleCancel()}>
            Cancelar baja programada
          </button>
        ) : (
          <button
            className="btn btn-danger"
            onClick={() => formik.handleSubmit()}
            disabled={loading}
          >
            Solicitar baja
          </button>
        )}
      </div>
    </React.Fragment>
  );
};

export default DeletePartner;
